import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { flushSync } from "react-dom";
import {
  CustomAdvancedMenuList,
  StyledDataGrid,
} from "../../../components/StyledComponents";
import {
  ClickAwayListener,
  Grow,
  LinearProgress,
  MenuItem,
  Paper,
  Popper,
} from "@mui/material";
import NoRowOverlay from "../../../assets/images/icons/NoRowOverlay";
import AddIcon from '@mui/icons-material/Add';
import {
  ControlpanelPrimaryButton,
  ControlpanelSuccess2thButton,
  ControlpanelSuccess3thButton,
} from "../../../components/StyledButton";
import SettingsIcon from "@mui/icons-material/Settings";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import CloudArrowUpIcon16pxSvg from "../../../assets/images/icons/CloudArrowUpIcon16pxSvg";
import CloudArrowDownIcon16pxSvg from "../../../assets/images/icons/CloudArrowDownIcon16pxSvg";
import { Link } from "@mui/material";
import { GridToolbarContainer } from "@mui/x-data-grid";
import SelectDataToImportUserGroupDialog from "./SelectDataToImportUserGroupDialog";
import SelectDataToExportUserGroupDialog from "./SelectDataToExportUserGroupDialog";
import AddUserGroupDialog from "./AddUserGroupDialog";
import CustomPagination from "../CustomPagination";
import { useTranslation } from "react-i18next";
import { getListGroup, exportUserGroupCSV } from "../../../axios/Group/GroupUrlApi";
import { format } from "date-fns";
import Cookies from "js-cookie";
import { useAuth } from "../../../context/AuthContext";
import { generateTranslateMuiTable } from "../../../utils/Function/commonFunction";
import CustomGridToolbarQuickFilter from "../CustomGridToolbarQuickFilter";
import { toast } from "react-toastify";
import { useLoading } from "../../../context/LoadingContext";
import CustomColumnsPanel from "../../../components/CustomColumnsPanel";
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import ServerSearchInput from "../../../components/ServerSearchInput";
import CustomSplitButton from "../../../components/CustomSplitButton";
import ImportIcon16pxSvg from "../../../assets/images/icons/ImportIcon16pxSvg";
import ExportIcon16pxSvg from "../../../assets/images/icons/ExportIcon16pxSvg";
dayjs.extend(utc);
dayjs.extend(timezone)


const MODE = {
  none: -1,
  import: 0,
  export: 1,
  delete: 2,
  invite: 3,
  create: 4,
  createFromDelete: 5,
  advanced: 6,
};

const UserGroupsGridInfoTable = ({
  onRowClick,
  refreshData,
  onRowDoubleClick,
  handleRefreshTable,
  userData,
  statusAddGroup,
  getStatusAddNewGroup,
}) => {
  const {
    genaralSettingDateFormat,
    genaralSettingTimeFormat,
    dateTimeFormat,
    profile,
  } = useAuth();
  const { t } = useTranslation();
  const [rowData, setRowData] = useState([]);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const [gridLoading, setGridLoading] = useState(false);
  const [filteredRowData, setFilteredRowData] = useState([]);
  const [filteredRowCount, setFilteredRowCount] = useState(0);
  const [rowCount, setRowCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [quickFilterText, setQuickFilterText] = useState("");
  const tableTranslated = useMemo(() => generateTranslateMuiTable(t), [t]);
  const anchorRefAdvanced = useRef(null);
  const [mode, setMode] = useState(MODE.none);
  const [show, hide] = useLoading();
  const [searchText, setSearchText] = useState("");
  const [debouncedSearchText, setDebouncedSearchText] = useState("");

  const [isSm, setIsSm] = useState(false);
  const [isMd, setIsMd] = useState(false);
  const [isLg, setIsLg] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      setIsSm(window.innerWidth < 540);
      setIsMd(window.innerWidth < 768);
      setIsLg(window.innerWidth < 992);
    };
    handleResize();
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const columnData = useMemo(
    () => [
      {
        field: "groupName",
        headerName: t("common.group-name"),
        minWidth: 170,
        flex: 1,
        hideable: true,
        valueFormatter: (value) => {
          if (!value) return "-";

          const normalized = String(value).trim().toLowerCase();

          if (normalized === "all users") return t("common.all-user");

          return value;
        }
      }
      ,
      {
        field: "remark",
        headerName: t("common.description"),
        minWidth: 170,
        flex: 1,
        hideable: true,
        valueFormatter: (value) => {
          if (!value) return "-";

          const normalized = String(value).trim().toLowerCase();

          if (normalized === "default group for all users")
            return t("common.default-group-for-all-users");

          return value;
        }
      },
      {
        field: "createdAt",
        headerName: t("common.created-time"),
        minWidth: 170,
        flex: 1,
        hideable: true,
        rowSpanValueGetter: (value, row) =>
          row ? `${row.id}-${row.createdAt}` : value,
        valueFormatter: (value) => {
          if (!value) return "-";

          // Parse như UTC
          const utcDate = dayjs.utc(value);  // tự parse "YYYY-MM-DD HH:mm:ss"

          // Chuyển sang giờ local của browser (+07:00)
          const localDate = utcDate.local();

          // Hard-code format đúng → test xem có ra 10/02/2026 11:39:00 không
          return localDate.format("DD/MM/YYYY HH:mm:ss");
        },
      },
      // {
      //   field: "createdBy",
      //   headerName: t("common.modified-time"),
      //   minWidth: 170,
      //   flex: 1,
      //   hideable: true,
      //   valueFormatter: (value) => value ? value : "-"
      // }

    ],
    [t, dateTimeFormat]
  );

  const defaultColumnVisibility = {
    groupName: true,
    remark: true,
    createdAt: false,
    createdBy: false,
  };

  // const handleRowClick = (params) => {
  //   const rowData = params.row;
  //   if (rowData) {
  //     onRowClick(rowData);
  //   }
  // };

  useEffect(() => {
    const fetchData = async () => {
      show();
      const payload = {
        pageNo: page + 1,
        recordPerPage: pageSize,
        langId: localStorage.getItem("i18nextLngId"),
        groupName: debouncedSearchText || null,
      };
      try {
        const response = await getListGroup(payload);
        // if (response.data.data.length > 0) {
        const defaultGroup = {
          id: 0,
          createdAt: null,
          createdBy: null,
          groupName: "All users",
          remark: "All users",
          profilePicture: null,
        };

        // const listUserGroup = [defaultGroup, ...response.data.data];
        const listUserGroup = response.data.data;

        setRowCount(response.data.pagination.totalElements);
        setTotalPages(response.data.pagination.totalPages);
        setRowData(listUserGroup);
        setFilteredRowData(listUserGroup);
        setFilteredRowCount(response.data.pagination.totalElements);
        if (listUserGroup.length > 0) {
          if (statusAddGroup) {
            onRowClick(listUserGroup[1]);
          } else {
            onRowClick(listUserGroup[0]);
          }
        }

        // }
      } catch (error) {
        if (error.response) {
          if (error.response.status !== 401) {
            toast.error(t(error?.response.data));
          }
        } else {
          toast.error(t('common.network-timeout'));
        }
      } finally {
        hide();
      }
    };

    fetchData();
  }, [page, pageSize, onRowClick, refreshData, t, show, hide, debouncedSearchText]);

  const handleQuickFilterChange = (searchText) => {
    const lowerSearch = searchText.toLowerCase();
    const filtered = rowData.filter((row) =>
      Object.values(row).some((val) =>
        String(val).toLowerCase().includes(lowerSearch)
      )
    );
    setFilteredRowData(filtered);
  };

  const handleRowClick = (params) => {
    const rowData = params.row;
    if (rowData) {
      onRowClick(rowData);
    }
  };

  const handleRowDoubleClick = (params) => {
    const rowData = params.row;
    if (rowData) {
      onRowDoubleClick(rowData);
    }
  };

  const [columnVisibilityModel, setColumnVisibilityModel] = useState(
    defaultColumnVisibility
  );
  const emailUser = profile?.email;
  const handleColumnVisibilityChange = (newModel) => {
    setColumnVisibilityModel(newModel);
    Cookies.set(
      `UserGroupsGridInfoTable_${emailUser}`,
      JSON.stringify(newModel),
      {
        expires: 90,
        path: "/",
      }
    );
  };

  useEffect(() => {
    const saved = Cookies.get(`UserGroupsGridInfoTable_${emailUser}`);
    if (saved) {
      setColumnVisibilityModel(JSON.parse(saved));
    }
  }, []);

  const [openAddUserGroup, setOpenAddUserGroup] = useState(false);

  const handleOpenAddUserGroup = () => {
    setOpenAddUserGroup(true);
  };

  const handleCloseAddUserGroup = () => {
    setOpenAddUserGroup(false);
  };

  // Tour: mở/đóng dialog thêm nhóm + advanced menu
  useEffect(() => {
    window.__openAddUserGroupDialog = () => {
      flushSync(() => {
        setMode(MODE.none);
        setOpenAddUserGroup(true);
      });
    };
    window.__closeAddUserGroupDialog = () => {
      flushSync(() => setOpenAddUserGroup(false));
    };
    window.__openUserGroupAdvancedMenu = () => {
      flushSync(() => {
        setOpenAddUserGroup(false);
        setMode(MODE.advanced);
      });
    };
    window.__closeUserGroupAdvancedMenu = () => {
      flushSync(() => setMode(MODE.none));
    };

    return () => {
      delete window.__openAddUserGroupDialog;
      delete window.__closeAddUserGroupDialog;
      delete window.__openUserGroupAdvancedMenu;
      delete window.__closeUserGroupAdvancedMenu;
    };
  }, []);

  const [openAdvanced, setOpenAdvanced] = useState(false);

  const handleToggleAdvanced = () => {
    setOpenAdvanced(!openAdvanced);
  };

  const handleCloseAdvanced = (event) => {
    if (
      anchorRefAdvanced.current &&
      anchorRefAdvanced.current.contains(event.target)
    ) {
      return;
    }
    setOpenAdvanced(false);
  };

  const [openSelectDataToImport, setOpenSelectDataToImport] = useState(false);

  const handleOpenSelectDataToImport = () => {
    setOpenSelectDataToImport(true);
  };

  const handleCloseSelectDataToImport = () => {
    setOpenSelectDataToImport(false);
  };

  const [openSelectDataToExport, setOpenSelectDataToExport] = useState(false);

  const handleOpenSelectDataToExport = () => {
    setOpenSelectDataToExport(true);
  };

  const handleCloseSelectDataToExport = () => {
    setOpenSelectDataToExport(false);
  };

  function getCurrentTimestamp() {
    const now = new Date();
    const yyyy = now.getFullYear();
    const MM = String(now.getMonth() + 1).padStart(2, '0');
    const dd = String(now.getDate()).padStart(2, '0');
    const HH = String(now.getHours()).padStart(2, '0');
    const mm = String(now.getMinutes()).padStart(2, '0');
    const ss = String(now.getSeconds()).padStart(2, '0');

    return `${yyyy}${MM}${dd}${HH}${mm}${ss}`;
  }

  const exportCSV = async () => {
    const payload = {
      page: totalPages,
      pageSize: pageSize,
      langId: localStorage.getItem("i18nextLngId"),
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      formatDate: dateTimeFormat,
      groupName: debouncedSearchText || null,
    };

    try {
      show();
      const response = await exportUserGroupCSV(payload);

      if (response) {
        const blob = new Blob([response.data], { type: 'application/octet-stream' });
        const url = window.URL.createObjectURL(blob);

        const a = document.createElement('a');
        a.href = url;
        const filename = `${t("export.export-user-group")}_${getCurrentTimestamp()}.xlsx`;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      if (error.response) {
        if (error.response.status !== 401) {
          toast.error(t(error?.response.data));
        }
      } else {
        toast.error(t('common.network-timeout'));
      }
    } finally {
      hide();
      setMode(MODE.none);
    }
  };

  const handleCloseDialog = () => {
    setMode(MODE.none);
  };

  const handleSearchChange = (value) => {
    setSearchText(value);
  };

  const encodedSearchText = useMemo(() => {
    return searchText;
  }, [searchText]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchText(encodedSearchText);
      setPage(0);
    }, 700);

    return () => clearTimeout(handler);
  }, [encodedSearchText]);

  const toolbarContent = useCallback(
    () => (
      <div className="flex justify-between items-center mb-3">
        <div data-tour="user-group-add-button">
          <CustomSplitButton
            label={t("button.add")}
            startIcon={<AddIcon />}
            onMainClick={handleOpenAddUserGroup}
            onDropdownClick={() => setMode(MODE.advanced)}
            ref={anchorRefAdvanced}
          />
        </div>
        <div className="ml-1 md:ml-0">
          {/* <CustomGridToolbarQuickFilter
            onSearchChange={handleQuickFilterChange}
          /> */}
          <ServerSearchInput fullWidth onChange={handleSearchChange}></ServerSearchInput>
        </div>
      </div>
    ),
    [t, anchorRefAdvanced]
  );

  const [filterModel, setFilterModel] = useState({
    items: [],
    quickFilterValues: [""],
  });

  // Khử dấu tiếng Việt + chuẩn hóa
  const normalizeVi = (v) =>
    String(v ?? "")
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/đ/g, "d");

  const handleFilterModelChange = (newModel) => {
    const raw = (newModel.quickFilterValues?.[0] ?? "").trim();
    if (!raw) {
      setFilteredRowData(rowData);
      setFilteredRowCount(rowCount);
      return;
    }

    const needle = normalizeVi(raw);
    const regex = new RegExp(`\\b${needle}\\b`, "i");

    const filtered = rowData.filter((row) =>
      Object.values(row).some((val) => {
        if (val == null || typeof val === "object") return false;
        const hay = normalizeVi(val);
        return regex.test(hay);
      })
    );

    setFilteredRowData(filtered);
    setFilteredRowCount(filtered.length);
  };

  const handleRowsPerPageChange = (newPageSize) => {
    // newPageSize ở đây tuỳ CustomPagination, có thể là event hoặc number
    const size =
      typeof newPageSize === "number"
        ? newPageSize
        : parseInt(newPageSize.target.value, 10);

    setPage((prevPage) => {
      // index của record đầu tiên đang xem
      const firstRowIndex = prevPage * pageSize;

      // page mới sao cho vẫn chứa record đó
      const newPage = Math.floor(firstRowIndex / size);

      // phòng trường hợp vượt quá số trang
      const maxPage = Math.max(
        0,
        Math.ceil(filteredRowCount / size) - 1
      );

      return Math.min(newPage, maxPage);
    });

    setPageSize(size);
  };


  return (
    <div>
      <StyledDataGrid
        sx={{
          height: "80vh",
          "& .MuiDataGrid-scrollbar--vertical, & .MuiDataGrid-scrollbar--horizontal":
          {
            display: "none !important",
          },

          /* KHÔNG chừa chỗ cho scrollbar nữa */
          "& .MuiDataGrid-scrollbarFiller": {
            display: "none !important",
          },
          "& .MuiDataGrid-columnHeaders": {
            paddingRight: 0,
          },
          "& .MuiDataGrid-topContainer": {
            paddingRight: 0,
          },

          "&": {
            "--DataGrid-scrollbarSize": "0px",
          },

          "& .MuiDataGrid-virtualScroller": {
            scrollbarWidth: "none",
          },
          "& .MuiDataGrid-virtualScroller::-webkit-scrollbar": {
            width: 0,
            height: 0,
          },
          "&:hover .MuiDataGrid-virtualScroller, &:focus-within .MuiDataGrid-virtualScroller":
          {
            scrollbarWidth: "thin",
          },
          "&:hover .MuiDataGrid-virtualScroller::-webkit-scrollbar, &:focus-within .MuiDataGrid-virtualScroller::-webkit-scrollbar":
          {
            width: 10,
            height: 10,
          },

          '& [class*="MuiDataGrid-scrollbar"][class*="--horizontal"]': {
            display: "none !important",
          },

          "& input[type=number]": {
            textAlign: "center",
            MozAppearance: "textfield",
            WebkitAppearance: "none",
            padding: "5px !important",
            height: "100%",
            pointerEvents: "none",
            caretColor: "transparent",
            userSelect: "none",
            paddingTop: "8px !important",
            paddingBottom: "8px !important"
          },
        }}
        rows={rowData}
        rowCount={rowCount}
        ignoreDiacritics
        paginationMode="client"
        columns={columnData}
        loading={gridLoading}
        getRowId={(r) => r.id}
        slots={{
          toolbar: toolbarContent,
          pagination: CustomPagination,
          noRowsOverlay: NoRowOverlay,
          columnsPanel: CustomColumnsPanel,
        }}
        slotProps={{
          pagination: {
            page,
            count: filteredRowCount,
            rowsPerPage: pageSize,
            onPageChange: setPage,
            onRowsPerPageChange: handleRowsPerPageChange,
          },
        }}
        localeText={tableTranslated}
        columnVisibilityModel={columnVisibilityModel}
        onColumnVisibilityModelChange={handleColumnVisibilityChange}
        onRowDoubleClick={handleRowDoubleClick}
        onRowClick={handleRowClick}
      />

      <AddUserGroupDialog
        open={openAddUserGroup}
        handleClose={handleCloseAddUserGroup}
        handleRefreshTable={handleRefreshTable}
        userData={userData}
        getStatusAddNewGroup={getStatusAddNewGroup}
      />
      {mode === MODE.advanced && (
        <Popper
          open
          anchorEl={anchorRefAdvanced.current}
          placement="bottom-start"
          transition
          disablePortal
          className="z-10"
        >
          {({ TransitionProps, placement }) => (
            <Grow
              {...TransitionProps}
              timeout={window.__tourIsOpen ? 0 : undefined}
              style={{
                transformOrigin:
                  placement === "bottom" ? "left top" : "left bottom",
              }}
            >
              <Paper style={{ borderRadius: "8px", }}>
                <ClickAwayListener
                  onClickAway={(e) => {
                    if (window.__tourIsOpen) return;
                    handleCloseDialog(e);
                  }}
                >
                  <CustomAdvancedMenuList
                    id="advanced-menu"
                    aria-labelledby="advanced-button"
                  >
                    <MenuItem
                      disabled
                      style={{
                        borderBottom: "1px solid #E5E7EB",
                        opacity: "0.5",
                        pointerEvents: "none",
                      }}
                      onClick={() => setMode(MODE.import)}
                    >
                      <Link>
                        <ImportIcon16pxSvg />
                        <p>{t("button.import-data")}</p>
                      </Link>
                    </MenuItem>
                    <MenuItem
                      data-tour="user-group-export-button"
                      style={{
                        borderBottom: "1px solid #E5E7EB",
                        pointerEvents: "auto",
                      }}
                      onClick={() => exportCSV()}
                    >
                      <Link>
                        <ExportIcon16pxSvg />
                        <p>{t("button.export-data")}</p>
                      </Link>
                    </MenuItem>
                  </CustomAdvancedMenuList>
                </ClickAwayListener>
              </Paper>
            </Grow>
          )}
        </Popper>
      )}
    </div>
  );
};

export default UserGroupsGridInfoTable;
